'use client';

import { useModals } from '@/contexts/ModalContext';
import { FinalScreen } from '@/app/[lang]/(main)/components/shared/scenarios/common/Screens/FinalScreen';
import { useTelegramTransactionsUserCheckStatus } from '@/generated';
import { Loading } from '@/app/[lang]/(main)/components/shared/scenarios/common/Screens/Loading';
import { useIsLoading, useUserData } from '@/contexts/LoginContext';
import { useRouter } from 'next/navigation';
import { PAYMENT_RETURN_PATH_KEY } from '@/app/[lang]/(main)/components/shared/scenarios/common/services';
import { AfterSubscriptionPaidScreen } from '@/modals/AfterSubscriptionPaidModal';
import { useDelayedFlag } from '@/hooks/useDelayedFlag.hook';

type Props = {
  removeFromUrl: () => void;
  afterPaymentId: string;
};

export const AfterCoinBought: React.FC<Props> = ({ removeFromUrl, afterPaymentId }) => {
  const modals = useModals();
  const router = useRouter();

  const { data, isLoading } = useTelegramTransactionsUserCheckStatus({ id: afterPaymentId });
  const userData = useUserData();
  const isUserLoading = useIsLoading();

  const redirectBackToPreviousPath = () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedPath = localStorage.getItem(PAYMENT_RETURN_PATH_KEY);
      if (!storedPath) {
        return;
      }

      localStorage.removeItem(PAYMENT_RETURN_PATH_KEY);
      router.push(storedPath);
    } catch (error) {
      console.warn('Unable to redirect to previous path', error);
    }
  };

  const onClose = () => {
    removeFromUrl();
    modals.close(`after-coin-bought-modal`);
    redirectBackToPreviousPath();
  };

  // Для подписки бэк уже опубликовал/промодерировал товар по productId — возвращаться
  // на страницу создания/редактирования, где юзер был до оплаты, не нужно, остаёмся
  // там, куда пришёл редирект с CardCom (my/products).
  const onCloseSubscription = () => {
    removeFromUrl();
    modals.close(`after-coin-bought-modal`);
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(PAYMENT_RETURN_PATH_KEY);
      } catch {
        // no-op
      }
    }
  };

  const isLoadingCombined = isLoading || isUserLoading;
  const showLoading = useDelayedFlag(isLoadingCombined);

  return (
    <>
      {isLoadingCombined ? (
        showLoading && <Loading onClose={onClose} />
      ) : data?.purpose === 'SUBSCRIPTION' && data.subscription ? (
        <AfterSubscriptionPaidScreen
          subscription={data.subscription}
          isTrial={data.isTrial}
          hasPendingProduct={!!data.product}
          onClose={onCloseSubscription}
        />
      ) : (
        <FinalScreen
          onClose={onClose}
          productId={data?.product?.id}
          resultBalance={String(userData?.balance) || '0'}
          amountPurchased={String(data?.amount) || '0'}
        />
      )}
    </>
  );
};
